import { Textarea, TextareaProps } from '@chakra-ui/react';
import { forwardRef, ReactNode, useId } from 'react';
import {
  B2B_FIELD_CONTROL_BORDER_RADIUS,
  B2B_FIELD_CONTROL_BORDER_WIDTH,
  B2B_FIELD_TEXTAREA_MAX_WIDTH,
  B2B_FIELD_TEXTAREA_MIN_HEIGHT,
  B2B_FIELD_TEXTAREA_PADDING,
} from '../b2bFormControlTokens';
import { B2bFieldLayout } from '../B2bFieldLayout';
import { b2bFieldFocusVisible } from 'styles/colors';

export type FieldTextareaProps = Omit<TextareaProps, 'id'> & {
  id?: string;
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  layoutDir?: 'ltr' | 'rtl';
};

/** Chakra `Textarea` with `forwardRef` for React Hook Form: `{...register('name')}`. */
export const FieldTextarea = forwardRef<HTMLTextAreaElement, FieldTextareaProps>(
  (
    {
      id: idProp,
      label,
      hint,
      error,
      isDisabled,
      isRequired,
      isReadOnly,
      isInvalid,
      rows = 4,
      layoutDir,
      ...textareaProps
    },
    ref,
  ) => {
    const autoId = useId();
    const fieldId = idProp ?? `b2b-field-textarea-${autoId}`;

    const fieldDir = layoutDir === 'rtl' ? 'rtl' : layoutDir === 'ltr' ? 'ltr' : 'auto';
    const textAlign =
      layoutDir === 'rtl' ? ('right' as const) : layoutDir === 'ltr' ? ('start' as const) : 'start';
    const placeholderSx =
      layoutDir === 'rtl'
        ? { color: 'dark.D400', textAlign: 'right' as const }
        : layoutDir === 'ltr'
        ? { color: 'dark.D400', textAlign: 'left' as const }
        : { color: 'dark.D400' };

    return (
      <B2bFieldLayout
        fieldId={fieldId}
        label={label}
        hint={hint}
        error={error}
        isDisabled={isDisabled}
        isRequired={isRequired}
        isReadOnly={isReadOnly}
        isInvalid={isInvalid}
      >
        <Textarea
          id={fieldId}
          ref={ref}
          rows={rows}
          dir={fieldDir}
          textAlign={textAlign}
          w='full'
          maxW={B2B_FIELD_TEXTAREA_MAX_WIDTH}
          display='block'
          minH={B2B_FIELD_TEXTAREA_MIN_HEIGHT}
          p={B2B_FIELD_TEXTAREA_PADDING}
          fontSize='md'
          lineHeight='1.5'
          borderRadius={B2B_FIELD_CONTROL_BORDER_RADIUS}
          borderWidth={B2B_FIELD_CONTROL_BORDER_WIDTH}
          borderColor='dark.D150'
          resize='vertical'
          _hover={{ borderColor: 'dark.D200' }}
          _placeholder={placeholderSx}
          _focusVisible={{
            ...b2bFieldFocusVisible,
          }}
          {...textareaProps}
        />
      </B2bFieldLayout>
    );
  },
);

FieldTextarea.displayName = 'FieldTextarea';
